/**
 * Attachments go to the portal, which writes them under
 * data/organizations/<id>/shared/crm/attachments/ — Nextcloud-visible, and
 * carried by `make backup-data`.
 *
 * Not a Supabase bucket, and deliberately not PostgREST either: file bytes
 * through a REST-over-SQL layer would land them in Postgres, which is what
 * Documenso does with its PDFs and why its database grows with volume.
 */

import { getCrmConfig } from "@/lib/crmConfig";

export type RAFileLike = {
  rawFile?: File;
  src?: string;
  title?: string;
  path?: string;
  type?: string;
};

export const uploadAttachment = async (file: RAFileLike): Promise<RAFileLike> => {
  if (!(file.rawFile instanceof File)) return file;
  const cfg = await getCrmConfig();

  const form = new FormData();
  form.append("file", file.rawFile);

  const res = await fetch(`${cfg.portalUrl}/api/crm/upload`, {
    method: "POST",
    body: form,
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`attachment upload failed: HTTP ${res.status}`);
  }
  const body = await res.json();

  // A PATH, never a URL. Attachment URLs are signed on read and expire, so
  // one stored in the note would be dead by the time anybody clicked it.
  // `signAttachments` turns paths into usable `src` values at render time.
  return {
    path: body.path,
    title: file.title ?? file.rawFile.name,
    type: file.rawFile.type,
  };
};

/** What a stored attachment looks like in a note, before it can be shown. */
export type StoredAttachment = {
  path?: string;
  title?: string;
  type?: string;
  size?: number;
  src?: string;
};

/**
 * Turn stored paths into short-lived signed URLs.
 *
 * Upstream's NoteAttachments renders `attachment.src` straight into an <img>
 * and an <a href>, neither of which can carry an Authorization header — so
 * the URL has to authorise itself. The portal signs (organization, path,
 * expiry) with a key the browser never sees; a link copied out of the page
 * stops working when it expires.
 *
 * Done here rather than by overriding NoteAttachments.tsx: that file is
 * upstream's, and forking it would put a permanent conflict in the way of
 * `git merge upstream`. This file is ours.
 *
 * One request per page of notes, and a failure is not fatal — a note whose
 * attachment cannot be signed still renders its text, which is most of the
 * value.
 */
export const signAttachments = async (
  records: any[],
  keycloakToken: () => Promise<string>,
): Promise<any[]> => {
  const paths = new Set<string>();
  for (const r of records ?? []) {
    for (const a of (r?.attachments ?? []) as StoredAttachment[]) {
      if (a?.path && !a.src) paths.add(a.path);
    }
  }
  if (paths.size === 0) return records;

  let urls: Record<string, string> = {};
  try {
    const cfg = await getCrmConfig();
    const res = await fetch(`${cfg.portalUrl}/api/crm/attachment-urls`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${await keycloakToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ paths: [...paths] }),
      credentials: "omit",
    });
    if (res.ok) urls = (await res.json()).urls ?? {};
  } catch {
    // Leave `src` unset: the attachment shows as un-openable rather than the
    // whole notes list failing to render.
    return records;
  }

  return (records ?? []).map((r) =>
    r?.attachments?.length
      ? {
          ...r,
          attachments: (r.attachments as StoredAttachment[]).map((a) =>
            a?.path && urls[a.path] ? { ...a, src: urls[a.path] } : a,
          ),
        }
      : r,
  );
};
